// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { z } from "zod";

import { ParsedRecord } from "../../../utils/types";
import { dateStringSchema } from "../../../utils/zod";
import { opportunitySchemaBase } from "../../utils/opportunitySchemaBase";
import {
  coverSheetInformationSchema,
  q1Notes,
  q2Notes,
  q7Notes,
  singleIncidentPeriodReportSchema,
  TrusteeFormSchema,
  trusteeFormSchema,
  UsTnCoverSheetSharedDraftData,
} from "../utils";

export const usTnInitialClassification2026Schema = opportunitySchemaBase.extend(
  {
    eligibleCriteria: z
      .object({
        custodyLevelIsNotMax: z.null(),
        notHasInitialClassificationInStatePrisonCustody: z.null(),
      })
      .partial()
      .passthrough(),
    ineligibleCriteria: z
      .object({
        custodyLevelIsNotMax: z.object({}).passthrough(),
        notHasInitialClassificationInStatePrisonCustody: z
          .object({})
          .passthrough(),
      })
      .partial()
      .passthrough(),
    formInformation: z
      .object({
        q3Score: z.coerce.number().nullable(),
        q4Score: z.coerce.number().nullable(),
        q5Score: z.coerce.number().nullable(),
        q6Score: z.coerce.number().nullable(),
        q7Score: z.coerce.number().optional().default(0),
        q1Notes,
        q2Notes,
        q3Notes: singleIncidentPeriodReportSchema,
        q4Notes: singleIncidentPeriodReportSchema,
        q5Notes: singleIncidentPeriodReportSchema,
        q7Notes,
        sixMonthsAgo: dateStringSchema.optional(),
        twelveMonthsAgo: dateStringSchema.optional(),
      })
      .merge(trusteeFormSchema)
      .merge(coverSheetInformationSchema),
  },
);

export type UsTnInitialClassification2026ReferralRecord = ParsedRecord<
  typeof usTnInitialClassification2026Schema
>;

export type UsTnInitialClassification2026FormInformation =
  UsTnInitialClassification2026ReferralRecord["output"]["formInformation"];

export type UsTnInitialClassification2026DraftData =
  UsTnInitialClassification2026FormInformation &
    UsTnCoverSheetSharedDraftData &
    TrusteeFormSchema & {
      q1Selection: number;
      q2Selection: number;
      q3Selection: number;
      q4Selection: number;
      q5Selection: number;
      q6Selection: number;
      q7Selection: number;
      q1aNotes: string;
      q1bNotes: string;
    };
